/**
 * Step 3 of signup: the DPA / ToS click-through and the FERPA acknowledgement
 * (AC-FERPA-ACK-001).
 *
 * Three independent gates must all be satisfied before "Create team portal"
 * enables:
 *   1. the reader reached the bottom of the combined DPA + ToS text,
 *   2. the FERPA acknowledgement checkbox is checked,
 *   3. the DPA/ToS acceptance checkbox is checked.
 *
 * The FERPA checkbox label contains the acknowledgement text and nothing else —
 * the signup smoke test asserts `label.textContent` equals
 * `FERPA_ACKNOWLEDGEMENT_TEXT` exactly, and merge is blocked on any deviation.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { LegalDocumentView } from '../../components/legal-document-view';
import { FERPA_ACKNOWLEDGEMENT_TEXT } from '../../lib/constants';
import { DPA_DOCUMENT, TOS_DOCUMENT } from '../../lib/legal-copy';

/** Pixels of slack allowed when deciding "reached the bottom". */
const SCROLL_BOTTOM_EPSILON = 24;

export interface ConsentSubmission {
  isFerpaAcknowledged: true;
  isDpaAccepted: true;
}

export interface DpaFerpaScreenProps {
  teamName: string;
  dpaVersion: string;
  isSubmitting: boolean;
  /** Server-side failure, already converted to a human-readable message. */
  submitError: string | null;
  onBack: () => void;
  onAccept: (submission: ConsentSubmission) => void;
}

export function DpaFerpaScreen({
  teamName,
  dpaVersion,
  isSubmitting,
  submitError,
  onBack,
  onAccept,
}: DpaFerpaScreenProps): JSX.Element {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const [isFerpaAcknowledged, setIsFerpaAcknowledged] = useState(false);
  const [isDpaAccepted, setIsDpaAccepted] = useState(false);
  const [wasSubmitAttempted, setWasSubmitAttempted] = useState(false);

  const evaluateScroll = useCallback((element: HTMLDivElement) => {
    const { scrollTop, scrollHeight, clientHeight } = element;
    // `scrollHeight === 0` means the browser has not laid the region out yet
    // (this is also the case in jsdom). Treating that as "no overflow" would
    // silently open the gate, so only a measured layout can satisfy it.
    if (scrollHeight === 0) return;
    if (scrollHeight - scrollTop - clientHeight <= SCROLL_BOTTOM_EPSILON) setHasReachedEnd(true);
  }, []);

  // A short document on a tall desktop screen never scrolls; re-measure on
  // mount and on resize so those readers are not locked out.
  useEffect(() => {
    const element = scrollRef.current;
    if (element === null) return;
    evaluateScroll(element);
    const onResize = (): void => evaluateScroll(element);
    globalThis.addEventListener?.('resize', onResize);
    return () => globalThis.removeEventListener?.('resize', onResize);
  }, [evaluateScroll]);

  const isReadyToAccept = hasReachedEnd && isFerpaAcknowledged && isDpaAccepted;

  function onSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setWasSubmitAttempted(true);
    if (!isReadyToAccept || isSubmitting) return;
    onAccept({ isFerpaAcknowledged: true, isDpaAccepted: true });
  }

  return (
    <form className="stack" onSubmit={onSubmit} noValidate>
      <div>
        <h2 className="auth__title">Review and accept</h2>
        <p className="muted">
          {teamName === '' ? 'Your program' : teamName} — Data Processing Addendum v{dpaVersion} and
          Terms of Service. Scroll to the end to enable acceptance.
        </p>
      </div>

      {submitError ? (
        <div className="banner banner--error" role="alert">
          <span className="banner__title">Team portal not created</span>
          <span>{submitError}</span>
        </div>
      ) : null}

      <div
        className="consent__scroll"
        data-testid="consent-scroll-region"
        ref={scrollRef}
        onScroll={(event) => evaluateScroll(event.currentTarget)}
        tabIndex={0}
        role="region"
        aria-label="Data Processing Addendum and Terms of Service"
      >
        <LegalDocumentView document={DPA_DOCUMENT} version={dpaVersion} />
        <LegalDocumentView document={TOS_DOCUMENT} version={dpaVersion} />
        <p className="muted" data-testid="consent-end-marker">
          End of the Data Processing Addendum and Terms of Service.
        </p>
      </div>

      <p className="consent__progress" data-complete={hasReachedEnd ? 'true' : 'false'}>
        <span aria-hidden="true">{hasReachedEnd ? '✓' : '↓'}</span>
        <span data-testid="consent-scroll-status">
          {hasReachedEnd
            ? 'You have reached the end of both documents.'
            : 'Keep scrolling — acceptance unlocks at the end of the documents.'}
        </span>
      </p>

      {/* Nothing but the acknowledgement text may live inside this label: the
          smoke test compares its textContent to FERPA_ACKNOWLEDGEMENT_TEXT. */}
      <label className="checkbox" data-testid="ferpa-acknowledgement-label">
        <input
          type="checkbox"
          name="ferpa_acknowledgement"
          data-testid="ferpa-acknowledgement-checkbox"
          checked={isFerpaAcknowledged}
          onChange={(event) => setIsFerpaAcknowledged(event.target.checked)}
        />
        <span className="checkbox__text">{FERPA_ACKNOWLEDGEMENT_TEXT}</span>
      </label>

      <label className="checkbox" data-testid="dpa-acceptance-label">
        <input
          type="checkbox"
          name="dpa_acceptance"
          data-testid="dpa-acceptance-checkbox"
          checked={isDpaAccepted}
          onChange={(event) => setIsDpaAccepted(event.target.checked)}
        />
        <span className="checkbox__text">
          I have read and accept the Data Processing Addendum (v{dpaVersion}) and the Terms of
          Service on behalf of my program, and I am authorised to do so.
        </span>
      </label>

      <ul className="consent__gate-list" aria-label="Acceptance requirements">
        <li data-met={hasReachedEnd ? 'true' : 'false'}>
          {hasReachedEnd ? '✓' : '○'} Read to the end of the documents
        </li>
        <li data-met={isFerpaAcknowledged ? 'true' : 'false'}>
          {isFerpaAcknowledged ? '✓' : '○'} FERPA acknowledgement checked
        </li>
        <li data-met={isDpaAccepted ? 'true' : 'false'}>
          {isDpaAccepted ? '✓' : '○'} DPA and Terms accepted
        </li>
      </ul>

      {wasSubmitAttempted && !isReadyToAccept ? (
        <p className="field__error" role="alert">
          Complete all three requirements above before creating the team portal.
        </p>
      ) : null}

      <div className="btn-row">
        <button
          type="submit"
          className="btn btn--primary"
          disabled={!isReadyToAccept || isSubmitting}
          data-testid="consent-accept-button"
        >
          {isSubmitting ? 'Creating team portal…' : 'Accept and create team portal'}
        </button>
        <button
          type="button"
          className="btn btn--secondary"
          onClick={onBack}
          disabled={isSubmitting}
        >
          Back
        </button>
      </div>
      <p className="field__hint">
        Your acceptance is recorded with the document version, the time, your IP address and your
        browser user agent, and is written to the tenant audit log.
      </p>
    </form>
  );
}
